import { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./config/authConfig";
//import { registerServiceWorker, setupPWAInstall } from "./utils/pwaUtils";
import { createIDBPersister } from "./utils/queryPersister";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { LazyLoadingSpinner } from "./components/LazyLoadingSpinner";
import authStore from "./stores/auth";

// Import global styles
import "./assets/css/index.css";
import { i18nContainer } from "./translation/i18n";

i18nContainer.init();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        if (error && typeof error === "object" && "status" in error) {
          const status = error.status as number;
          if (status >= 400 && status < 500 && status !== 401) {
            return false;
          }
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 5000)
    },
    mutations: {
      retry: false
    }
  }
});

const persister = createIDBPersister();

// Initialize PWA features
// registerServiceWorker();
// setupPWAInstall();

// Set up a Router instance
const router = createRouter({
  routeTree: routeTree,
  context: {
    queryClient,
    isAuthenticated: false
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const RootComponent = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      await authStore.getState().checkAuthStatus();
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const renderContent = useMemo(() => {
    if (isLoading) {
      return <LazyLoadingSpinner />;
    }
    return (
      <RouterProvider
        router={router}
        context={{ isAuthenticated: authStore.getState().isAuthenticated }}
      />
    );
  }, [isLoading]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000
      }}
    >
      {renderContent}
    </PersistQueryClientProvider>
  );
};

const rootElement = document.getElementById("root");
if (rootElement && !(rootElement as any)._reactRootContainer) {
  const root = createRoot(rootElement);
  root.render(
    <MsalProvider instance={msalInstance}>
      <RootComponent />
    </MsalProvider>
  );
}
